// components/notifications/EnhancedNotificationSystem.jsx
import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Badge,
  Divider,
  Typography,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useNotifications } from './contexts/NotificationContext';
import InvitationNotificationCard from './invitation/InvitationNotificationCard';
import JoinRequestNotificationCard from './invitation/JoinRequestNotificationCard';
import SuggestionNotificationCard from './invitation/SuggestionNotificationCard';
import MeetingRequestNotificationCard from './meeting/MeetingRequestNotificationCard';
import CommentNotificationCard from './comment/CommentNotificationCard';
import LikeNotificationCard from './like/LikeNotificationCard';
import MessageNotificationCard from 'components/common/layout/dropdowns/MessageNotificationCard';
import { useUser } from 'shared/helpers/axios';
import FollowNotificationCard from './follow/FollowNotificationCard';
import VerificationBonusNotificationCard from './bonus/VerificationBonusNotificationCard';
import CallUpdateNotificationCard from './call/CallUpdateNotificationCard';
import ModerationWarningNotificationCard from './system/ModerationWarningNotificationCard';
import ModerationReportNotificationCard from './system/ModerationReportNotificationCard';
import TaskAssignedNotificationCard from './proposal/TaskAssignedNotificationCard';
import TodoAssignedNotificationCard from './proposal/TodoAssignedNotificationCard';
import ProposalChatNotificationCard from './proposal/ProposalChatNotificationCard';
import ManuscriptEditNotificationCard from './proposal/ManuscriptEditNotificationCard';
import LabNotificationCard from './lab/LabNotificationCard';
import EventNotificationCard from './event/EventNotificationCard';
import SecurityAlertCard from './security/SecurityAlertCard';
import PositionNotificationCard from './position/PositionNotificationCard';
import MentionNotificationCard from './mention/MentionNotificationCard';
import ConnectionNotificationCard from './connection/ConnectionNotificationCard';
import RoleAssignedNotificationCard from './proposal/RoleAssignedNotificationCard';
import ProposalReinviteNotificationCard from './proposal/ProposalReinviteNotificationCard';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';


const norm = (v) => (v || '').toString().toLowerCase();

const isCollaboration = (n) => {
  const t = norm(n.ntype);
  return t === 'consortium_invitation' || t === 'consortium_update' || t === 'invitation';
};
const isJoinRequest = (n) => norm(n.ntype) === 'consortium_join_request';
const isSuggestion = (n) => norm(n.ntype).includes('suggestion');
const isMeetingRequest = (n) => norm(n.ntype) === 'meeting_request';
const isMeetingPoll = (n) => norm(n.ntype) === 'meeting_poll';
const isMeeting = (n) => isMeetingRequest(n) || isMeetingPoll(n);
const isComment = (n) => norm(n.ntype) === 'comment';
const isLike = (n) => {
  const t = norm(n.ntype);
  return t === 'like' || t === 'content_liked' || t.includes('like');
};
const isMessage = (n) => norm(n.ntype) === 'message';

const isFollow = (n) => norm(n.ntype) === 'follow';
const isVerificationBonus = (n) => norm(n.ntype) === 'verification_bonus';
const isCallUpdate = (n) => norm(n.ntype) === 'call_update';
const isSystem = (n) => norm(n.ntype) === 'system';
const isModReport = (n) => norm(n.ntype) === 'moderation_report';
const isTaskAssigned = (n) => norm(n.ntype) === 'task_assigned';
const isTodoAssigned = (n) => norm(n.ntype) === 'todo_assigned';
const isProposalChat = (n) => norm(n.ntype) === 'proposal_chat';
const isManuscriptEdit = (n) => norm(n.ntype) === 'proposal_update' && n.payload?.kind === 'manuscript_edit';
const isRoleAssigned = (n) => norm(n.ntype) === 'role_assigned';

const TABS = [
  { key: 'all',           label: 'All',           filter: () => true },
  { key: 'unread',        label: 'Unread',        filter: (n) => !n.is_read },
  { key: 'collaboration', label: 'Collaborations', filter: (n) => isCollaboration(n) || norm(n.ntype) === 'proposal_reinvite' },
  // { key: 'message',      label: 'Messages',      filter: (n) => isMessage(n) },
  // { key: 'suggestions',   label: 'Suggestions',   filter: (n) => isSuggestion(n) },
  // { key: 'meetings',      label: 'Meetings',      filter: (n) => isMeeting(n) },
  // { key: 'likes',         label: 'Likes',         filter: (n) => isLike(n) },
  // { key: 'follows',         label: 'Follows',     filter: (n) => isFollow(n) },
];

const EnhancedNotificationSystem = () => {
  const { notifications = [], handleNotificationAction, markAsRead } = useNotifications();
  useUser(); // if needed for role-specific behavior later

  // --- Derive unified list (you already had two arrays; we can just use all and filter) ---
  const sorted = useMemo(
    () =>
      [...notifications].sort((a, b) => new Date(b.created) - new Date(a.created)),
    [notifications]
  );

  // --- Counts for badges (fast and memoized) ---
  const counts = useMemo(() => {
    const base = {
      all: sorted.length,
      unread: sorted.filter((n) => !n.is_read).length,
      collaboration: sorted.filter((n) => isCollaboration(n) || norm(n.ntype) === 'proposal_reinvite').length,
      messages: sorted.filter(isMessage).length,
      suggestions: sorted.filter(isSuggestion).length,
      meetings: sorted.filter(isMeeting).length,
      likes: sorted.filter(isLike).length,
    };
    return base;
  }, [sorted]);

  // --- UI state ---
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');

  // --- Simple text search across a few likely fields ---
  const matchesQuery = useCallback(
    (n) => {
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      const payload = n.payload || {};
      const hay = [
        n.ntype,
        n.message,
        n.title,
        payload.consortium_name,
        payload.from_user_name,
        payload.to_user_name,
        payload.recipient_name,
        payload.action,
        payload.invitation_status,
      ]
        .filter(Boolean)
        .map(String)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    },
    [q]
  );

  // --- Active filter per tab ---
  const activeFilter = useMemo(() => {
    const def = TABS.find((t) => t.key === tab) || TABS[0];
    return def.filter;
  }, [tab]);

  // --- Final list displayed ---
  const visible = useMemo(
    () => sorted.filter(activeFilter).filter(matchesQuery),
    [sorted, activeFilter, matchesQuery]
  );

  // --- Mark all visible as read (only those currently visible & unread) ---
  const markAllVisibleAsRead = useCallback(async () => {
    const targets = visible.filter((n) => !n.is_read);
    for (const n of targets) {
      try {
        await markAsRead(n.id);
      } catch (e) {
        // noop; best-effort
      }
    }
  }, [visible, markAsRead]);

  // --- Renderer for each notification type ---
  const renderNotification = (n) => {

    if (isMessage(n)) {
      // Transform notification payload to message format for MessageNotificationCard
      const payload = n.payload || {};
      const messageData = {
        id: payload.message_id || n.id,
        is_read: n.is_read,
        created: n.created,
        created_at: payload.sent_at || n.created,
        subject: payload.subject,
        preview: payload.preview,
        body_preview: payload.preview,
        sender_actor: payload.sender ? {
          display_name: payload.sender.name,
          actor_type: payload.sender.type,
          avatar_url: payload.sender.avatar,
          entity_id: payload.sender.id
        } : null,
        partner_actor: payload.recipient ? {
          display_name: payload.recipient.name,
          actor_type: payload.recipient.type,
          entity_id: payload.recipient.id
        } : (payload.sender ? {
          display_name: payload.sender.name,
          actor_type: payload.sender.type,
          avatar_url: payload.sender.avatar,
          entity_id: payload.sender.id
        } : null),
        is_consortium_message: payload.is_consortium_message || false,
        is_organization_message: payload.is_organization_message || false
      };
      
      return (
        <MessageNotificationCard
          key={n.id}
          message={messageData}
          onMarkAsRead={() => markAsRead(n.id)}
          onMessageClick={(msg) => {
            // Navigate to messages page
            window.location.href = '/dashboard/messages';
          }}
        />
      );
    }
    if (isJoinRequest(n)) {
      return (
        <JoinRequestNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
          handleNotificationAction={handleNotificationAction}
        />
      );
    }
    if (isLike(n)) {
      return (
        <LikeNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }
    if (isComment(n)) {
      return (
        <CommentNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }
    if (isMeetingRequest(n) || isMeetingPoll(n)) {
      return (
        <MeetingRequestNotificationCard
          key={n.id}
          notification={n}
          onMarkAsRead={() => markAsRead(n.id)}
          handleNotificationAction={handleNotificationAction}
        />
      );
    }
    if (isSuggestion(n)) {
      return (
        <SuggestionNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
          handleNotificationAction={handleNotificationAction}
        />
      );
    }


    if (isSuggestion(n)) {
      return (
        <SuggestionNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
          handleNotificationAction={handleNotificationAction}
        />
      );
    }

    if (isVerificationBonus(n)) {
      return (
        <VerificationBonusNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isFollow(n)) {
      return (
        <FollowNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isModReport(n)) {
      return (
        <ModerationReportNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isSystem(n)) {
      return (
        <ModerationWarningNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isCallUpdate(n)) {
      return (
        <CallUpdateNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isTaskAssigned(n)) {
      return (
        <TaskAssignedNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isTodoAssigned(n)) {
      return (
        <TodoAssignedNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isProposalChat(n)) {
      return (
        <ProposalChatNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (['follow_request', 'follow_accepted'].includes(norm(n.ntype))) {
      return <ConnectionNotificationCard key={n.id} notification={n} onMarkRead={() => markAsRead(n.id)} />;
    }

    if (norm(n.ntype) === 'mention') {
      return <MentionNotificationCard key={n.id} notification={n} onMarkRead={() => markAsRead(n.id)} />;
    }

    if (norm(n.ntype) === 'position_update') {
      return <PositionNotificationCard key={n.id} notification={n} onMarkRead={() => markAsRead(n.id)} />;
    }

    if (norm(n.ntype) === 'security_alert') {
      return <SecurityAlertCard key={n.id} notification={n} onMarkRead={() => markAsRead(n.id)} />;
    }

    if (norm(n.ntype) === 'lab_update') {
      return <LabNotificationCard key={n.id} notification={n} onMarkRead={() => markAsRead(n.id)} />;
    }

    if (norm(n.ntype) === 'event_update') {
      return <EventNotificationCard key={n.id} notification={n} onMarkRead={() => markAsRead(n.id)} />;
    }

    if (isManuscriptEdit(n)) {
      return (
        <ManuscriptEditNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (isRoleAssigned(n)) {
      return (
        <RoleAssignedNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
        />
      );
    }

    if (norm(n.ntype) === 'proposal_reinvite') {
      return (
        <ProposalReinviteNotificationCard
          key={n.id}
          notification={n}
          onMarkRead={() => markAsRead(n.id)}
          handleNotificationAction={handleNotificationAction}
        />
      );
    }

    // default to collaboration/invitation card
    return (
      <InvitationNotificationCard
        key={n.id}
        notification={n}
        onMarkRead={() => markAsRead(n.id)}
        handleNotificationAction={handleNotificationAction}
      />
    );
  };

  return (
    <PageContainer>
      {/* tabs after the title, search and "mark all as read" at the right: the Collaboration Hub's header */}
      <PageHeader title="Notifications" actions={(
        <>
            <TextField
              size="small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search notifications…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />

            <Button
              variant="outlined"
              startIcon={<MarkEmailReadIcon />}
              onClick={markAllVisibleAsRead}
              disabled={!visible.some((n) => !n.is_read)}
            >
              Mark all as read
            </Button>
        </>
      )}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ ...HEADER_TABS_SX, flex: 1, minWidth: 0, '& .MuiTab-root': { ...HEADER_TABS_SX['& .MuiTab-root'], pr: 3.5 } }}
            >
              {TABS.map(({ key, label }) => (
                <Tab
                  key={key}
                  value={key}             

                  label={
                    <Badge
                      color="primary"
                      badgeContent={counts[key] || 0}
                      max={999}
                      showZero
                      sx={{
                        '& .MuiBadge-badge': {
                          transform: 'scale(0.85) translate(95%, -45%)',
                          fontSize: '0.9rem',
                        },
                      }}
                    >
                       <span style={{ fontSize: '1.15rem' }}>{label}</span>
                    </Badge>
                  }
                 
                />
              ))}
            </Tabs>
      </PageHeader>

      {/* <Divider /> */}

      {/* List */}
      <Box sx={{ p: 1.5 }}>
        {visible.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              No notifications
            </Typography>
            <Typography variant="body2">
              Try a different tab or clear the search filter.
            </Typography>
          </Box>
        ) : (
          visible.map(renderNotification)
        )}
      </Box>
    </PageContainer>
  );
};

export default EnhancedNotificationSystem;
